// src/Pages/Assets/futuresComponentes/pnlUtils.js

// === Palette (Binance-like) ===
export const BN_YELLOW = '#F0B90B';
export const GREEN = '#0ECB81';
export const RED = '#F6465D';

// === Format helpers ===
export const fmt = (n, d = 2) =>
  typeof n === 'number' && isFinite(n) ? n.toFixed(d) : '-';

export const toDateStr = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return '-'; }
};

// === Symbol resolution (أقوى مع بدائل كثيرة) ===
export const symbolFromPair = (row, pairIdToSymbol = {}) => {
  const c = (v) => (typeof v === 'string' ? v.toUpperCase() : v);

  const fromField = row?.trading_pair_symbol && c(String(row.trading_pair_symbol));
  const fromMap   = row?.trading_pair_id && pairIdToSymbol?.[String(row.trading_pair_id)];
  const fromObj   = typeof row?.trading_pair_id === 'object' && row?.trading_pair_id?.symbol && c(String(row.trading_pair_id.symbol));

  // بدائل شائعة في الـ history
  const fromSymbol     = row?.symbol && c(String(row.symbol));
  const fromPair       = row?.pair && c(String(row.pair));
  const fromPairSymbol = row?.pair_symbol && c(String(row.pair_symbol));
  const fromTrading    = row?.trading_pair?.symbol && c(String(row.trading_pair.symbol));

  // محاولة تركيب الرمز من base_asset
  const fromBaseObj = (row?.trading_pair_id?.base_asset_id?.symbol && c(String(row.trading_pair_id.base_asset_id.symbol) + 'USDT'));

  return fromField || fromMap || fromObj || fromSymbol || fromPair || fromPairSymbol || fromTrading || fromBaseObj || '';
};

// === PnL / Margin math ===
export const unrealizedPnL = ({ side, entry, current, qty }) => {
  if (!entry || !current || !qty) return 0;
  return side === 'Long' ? (current - entry) * qty : (entry - current) * qty;
};

export const initialMargin = ({ entry, qty, lev }) => {
  if (!entry || !qty || !lev) return 0;
  return (entry * qty) / lev;
};

// === Inject computed fields for open positions ===
export const injectOpenComputed = (rows = [], pairIdToSymbol = {}, coinPrices = {}) =>
  (rows || []).map((t) => {
    const symbol = symbolFromPair(t, pairIdToSymbol);
    const entry = t?.open_price ?? t?.avg_open_price ?? null;
    const qty   = t?.amount ?? t?.qty ?? null;
    const lev   = t?.leverage ?? t?.lev ?? null;
    const current =
      symbol && typeof coinPrices[symbol] === 'number' ? coinPrices[symbol] : entry;

    const pnl = unrealizedPnL({ side: t?.position, entry, current, qty });
    const im  = initialMargin({ entry, qty, lev });
    const roe = im > 0 ? (pnl / im) * 100 : 0;

    return { ...t, _symbol: symbol || '-', _current: current, _pnl: pnl, _roe: roe, _im: im };
  });

// === Inject computed for history (symbol + realized pnl + roe) ===
export const injectHistoryComputed = (rows = [], pairIdToSymbol = {}) =>
  (rows || []).map((r) => {
    const symbol = symbolFromPair(r, pairIdToSymbol) || '-';

    // مرونة بأسماء الحقول
    const amount = r?.amount ?? r?.qty ?? r?.position_size ?? null;
    const open   = r?.open_price ?? r?.avg_open_price ?? r?.entry_price ?? null;
    const close  = r?.close_price ?? r?.executed_close_price ?? r?.exit_price ?? r?.tp_hit_price ?? r?.sl_hit_price ?? null;
    const lev    = r?.leverage ?? r?.lev ?? null;
    const status = r?.status;

    let realized = null;
    if ((status === 'Closed' || status === 'Liquidated') && amount && open && close) {
      realized = r?.position === 'Long'
        ? (close - open) * amount
        : (open - close) * amount;
    } else if (status === 'Cancelled' || status === 'Canceled') {
      realized = 0;
    }

    const im  = (open && amount && lev) ? (open * amount) / lev : null;
    const roe = (im && realized != null) ? (realized / im) * 100 : null;

    return { ...r, _symbol: symbol, _realized: realized, _im: im, _roe: roe };
  });

// === Realized PnL from history (Closed/Liquidated only) ===
export const realizedPnLFromHistory = (rows = []) =>
  (rows || []).reduce((acc, r) => {
    const amount = r?.amount ?? r?.qty ?? null;
    const open   = r?.open_price ?? r?.avg_open_price ?? null;
    const close  = r?.close_price ?? r?.executed_close_price ?? null;
    const status = r?.status;
    if (!(status === 'Closed' || status === 'Liquidated')) return acc;
    if (!amount || !open || !close) return acc;
    const signed = r?.position === 'Long'
      ? (close - open) * amount
      : (open - close) * amount;
    return acc + signed;
  }, 0);

// === Daily realized PnL (grouped) + cumulative ===
export const dailyRealized = (rows = []) => {
  const dayMap = {};
  (rows || []).forEach((r) => {
    const amount = r?.amount ?? r?.qty ?? null;
    const open   = r?.open_price ?? r?.avg_open_price ?? null;
    const close  = r?.close_price ?? r?.executed_close_price ?? null;
    const status = r?.status;
    if (!(status === 'Closed' || status === 'Liquidated')) return;
    if (!amount || !open || !close) return;

    const day = (r?.created_at || r?.updated_at || '').slice(0, 10) || 'Unknown';
    const signed = r?.position === 'Long' ? (close - open) * amount : (open - close) * amount;
    dayMap[day] = (dayMap[day] || 0) + signed;
  });

  const arr = Object.entries(dayMap)
    .map(([date, pnl]) => ({ date, pnl }))
    .sort((a, b) => a.date.localeCompare(b.date));

  let cum = 0;
  return arr.map((d) => ({ ...d, cum: (cum += d.pnl) }));
};
